import { useTheme } from "@/theme/ThemeProvider";
import { useStyles } from "@/theme/styles";
import { useEffect } from "react";
import { Platform, Pressable, Text, View } from "react-native";
import { useNetworkStatus } from "@/network/NetworkContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getOfflineStore } from "@/offline/store";
import { runOfflineSync } from "@/offline/sync/gateway";
import { OFFLINE_BANNER_MESSAGE, OFFLINE_BANNER_SIMULATED_MESSAGE } from "@/offline/sync/hints";

export function OfflineBanner() {
  const { isOnline, simulateOffline, setSimulateOffline } = useNetworkStatus();
  const { theme } = useTheme();
  const s = useStyles();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["offline", "outbox-count"],
    queryFn: async () => {
      const store = await getOfflineStore();
      const items = await store.getOutbox();
      return {
        pending: items.filter((i) => i.status === "pending" || i.status === "error")
          .length,
        conflict: items.filter((i) => i.status === "conflict").length,
        quarantine: items.filter((i) => i.status === "quarantine").length,
      };
    },
    refetchInterval: 4000,
  });
  const pending = q.data?.pending ?? 0;
  const conflict = q.data?.conflict ?? 0;
  const quarantine = q.data?.quarantine ?? 0;
  const showOnlineStatus = isOnline && (pending > 0 || conflict > 0 || quarantine > 0);

  useEffect(() => {
    if (Platform.OS !== "web" || pending <= 0 || typeof window === "undefined") return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [pending]);

  if (isOnline && !showOnlineStatus) return null;

  return (
    <View style={s.alertWarning}>
      <Text style={s.alertWarningText}>
        {!isOnline
          ? simulateOffline
            ? OFFLINE_BANNER_SIMULATED_MESSAGE
            : OFFLINE_BANNER_MESSAGE
          : `Sync: ${pending} offen, ${conflict} Konflikte, ${quarantine} in Quarantäne.`}
      </Text>
      {Platform.OS === "web" && pending > 0 ? (
        <Text style={[s.alertWarningText, { marginTop: theme.space.xs }]}>
          Nicht gesendete Einträge gehen beim Schließen oder Neuladen verloren. Bitte die Seite
          bis zur Synchronisierung offen lassen.
        </Text>
      ) : null}
      <View
        style={{
          flexDirection: "row",
          gap: theme.space.xs,
          marginTop: theme.space.xs,
          flexWrap: "wrap",
        }}
      >
        {isOnline ? (
          <Pressable
            onPress={() => {
              void runOfflineSync()
                .then(() => {
                  void qc.invalidateQueries({ queryKey: ["offline"] });
                })
                .catch((err: unknown) => {
                  console.error(
                    "OfflineBanner: sync failed",
                    err instanceof Error ? err.message : err,
                  );
                });
            }}
            style={{
              paddingVertical: theme.space.xs,
              paddingHorizontal: theme.space.sm,
              borderRadius: theme.radius.sm,
              backgroundColor: theme.color.surface,
            }}
          >
            <Text
              style={{
                color: theme.color.primary,
                fontWeight: "700",
                fontSize: theme.fontSize.small,
              }}
            >
              Jetzt synchronisieren
            </Text>
          </Pressable>
        ) : null}
        {__DEV__ && simulateOffline ? (
          <Pressable
            onPress={() => setSimulateOffline(false)}
            style={{
              paddingVertical: theme.space.xs,
              paddingHorizontal: theme.space.sm,
              borderRadius: theme.radius.sm,
              backgroundColor: theme.color.surface,
            }}
          >
            <Text
              style={{
                color: theme.color.primary,
                fontSize: theme.fontSize.small,
                fontWeight: "700",
              }}
            >
              Simulation beenden
            </Text>
          </Pressable>
        ) : null}
      </View>
    </View>
  );
}
